/* gsap-animations.js (smooth + calm)
   - Timeline: .lotus-timeline-section / .js-lotus-fade (unchanged)
   - Global card reveal: .gsap-card-reveal (container) + .js-gsap-card (items)
   - Lotus USP dual images: .lotus-usp-dual + .js-lusp-image + .js-lusp-anim
   - Lotus promo marquee: .lotus-promo-marquee + .lotus-promo-marquee__rail (infinite xPercent loop)
   - Uses ScrollTrigger.batch for subtle, unobtrusive reveals
   - Shopify editor safe (section load/unload)
*/

(() => {
  if (typeof window === "undefined") return;

  const TIMELINE_SECTION_SELECTOR = ".lotus-timeline-section";
  const TIMELINE_TARGET_SELECTOR = ".js-lotus-fade";

  // ✅ GLOBAL card reveal selectors (use these anywhere)
  const CARD_GRID_SELECTOR = ".gsap-card-reveal";
  const CARD_ITEM_SELECTOR = ".js-gsap-card";

  // Lotus USP dual images (sections/lotus-usp-dual-cards.liquid)
  const USP_DUAL_SECTION_SELECTOR = ".lotus-usp-dual";
  const USP_DUAL_IMAGE_SELECTOR = ".js-lusp-image";
  const USP_DUAL_ANIM_SELECTOR = ".js-lusp-anim";

  // Lotus Features Carousel (sections/lotus-features-carousel.liquid)
  const FC_SECTION_SELECTOR = ".lotus-fc";
  const FC_HEADING_SELECTOR = ".js-fc-heading";

  // Lotus Loyalty Accordion (sections/lotus-loyalty-accordion.liquid)
  const LA_SECTION_SELECTOR = ".lotus-la";
  const LA_IMAGE_SELECTOR = ".js-la-image";
  const LA_ANIM_SELECTOR = ".js-la-anim";


  const triggersBySection = new WeakMap();

  const prefersReduced =
    window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  // ---------- Helpers ----------
  function qsa(root, sel) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  function ensureGsap() {
    if (typeof gsap === "undefined" || typeof ScrollTrigger === "undefined") return false;
    gsap.registerPlugin(ScrollTrigger);
    return true;
  }

  function rememberTrigger(holderEl, trigger) {
    if (!holderEl || !trigger) return;
    var arr = triggersBySection.get(holderEl);
    if (!arr) arr = [];
    arr.push(trigger);
    triggersBySection.set(holderEl, arr);
  }

  function cleanup(holderEl) {
    const triggers = triggersBySection.get(holderEl);
    if (triggers && Array.isArray(triggers)) {
      triggers.forEach((t) => t?.kill?.());
    }
    triggersBySection.delete(holderEl);
  }

  // Calm reveal utility
  function reveal(batch, opts) {
    opts = opts || {};
    gsap.to(batch, {
      opacity: 1,
      y: 0,
      duration: opts.duration || 0.9,
      ease: opts.ease || "power2.out",
      stagger: opts.stagger || 0.07,
      overwrite: "auto",
      clearProps: "willChange",
    });
  }

  // ---------- Timeline (existing) ----------
  function initTimelineSection(sectionEl) {
    if (!sectionEl) return;

    const targets = qsa(sectionEl, TIMELINE_TARGET_SELECTOR);
    if (!targets.length) return;

    if (prefersReduced) {
      if (typeof gsap !== "undefined") gsap.set(targets, { opacity: 1, y: 0 });
      return;
    }

    if (!ensureGsap()) return;

    // allow re-init
    cleanup(sectionEl);

    gsap.set(targets, { opacity: 0, y: 14, willChange: "transform,opacity" });

    const st = ScrollTrigger.batch(targets, {
      start: "top 90%",
      end: "top 65%",
      scrub: 0.6,
      once: true,
      onEnter: function (batch) {
        gsap.to(batch, {
          opacity: 1,
          y: 0,
          duration: 1.2,
          ease: "power3.out",
          stagger: 0.14,
          overwrite: "auto",
          clearProps: "willChange",
        });
      },
      onLeaveBack: function (batch) {
        gsap.to(batch, {
          opacity: 0,
          y: 14,
          duration: 0.5,
          ease: "power2.out",
          overwrite: "auto",
        });
      },
    });

    if (Array.isArray(st)) st.forEach((t) => rememberTrigger(sectionEl, t));
    else rememberTrigger(sectionEl, st);
  }

  function initGsapTimeline(root) {
    qsa(root, TIMELINE_SECTION_SELECTOR).forEach(function (sectionEl) {
      if (sectionEl.dataset.gsapTimelineAnimated === "true") return;
      sectionEl.dataset.gsapTimelineAnimated = "true";
      initTimelineSection(sectionEl);
    });
  }

  // ---------- ✅ Global card reveals (use anywhere) ----------
  function initGsapCardReveals(root) {
    qsa(root, CARD_GRID_SELECTOR).forEach(function (grid) {
      if (grid.dataset.gsapCardRevealAnimated === "true") return;
      grid.dataset.gsapCardRevealAnimated = "true";

      var cards = qsa(grid, CARD_ITEM_SELECTOR);
      if (!cards.length) return;

      if (prefersReduced) {
        if (typeof gsap !== "undefined") gsap.set(cards, { opacity: 1, y: 0 });
        return;
      }

      if (!ensureGsap()) return;

      // allow re-init (theme editor)
      cleanup(grid);

      // initial state
      gsap.set(cards, { opacity: 0, y: 14, willChange: "transform,opacity" });

      // Start when EACH card hits viewport (batch trigger uses each card as trigger internally)
      var st = ScrollTrigger.batch(cards, {
        start: "top 90%",
        once: true,
        onEnter: function (batch) {
          reveal(batch, { stagger: 0.07, duration: 0.9, ease: "power2.out" });
        },
      });

      // Normalize storing
      if (Array.isArray(st)) {
        st.forEach(function (t) {
          rememberTrigger(grid, t);
        });
      } else {
        rememberTrigger(grid, st);
      }

      // layout shifts
      setTimeout(function () {
        if (typeof ScrollTrigger !== "undefined") ScrollTrigger.refresh();
      }, 250);
    });
  }

  // ---------- Lotus USP dual images ----------
  function initLotusUspDualSection(sectionEl) {
    if (!sectionEl) return;

    const images = qsa(sectionEl, USP_DUAL_IMAGE_SELECTOR);
    const animEls = qsa(sectionEl, USP_DUAL_ANIM_SELECTOR);
    if (!images.length && !animEls.length) return;

    if (prefersReduced) {
      if (typeof gsap !== "undefined") {
        gsap.set(images, { opacity: 1, y: 0 });
        gsap.set(animEls, { opacity: 1, y: 0 });
      }
      return;
    }

    if (!ensureGsap()) return;

    cleanup(sectionEl);

    if (images.length) {
      gsap.set(images, { opacity: 0, y: 28, willChange: "transform,opacity" });
      var stImg = ScrollTrigger.batch(images, {
        start: "top 90%",
        once: true,
        onEnter: function (batch) {
          gsap.to(batch, {
            opacity: 1,
            y: 0,
            duration: 0.95,
            ease: "power3.out",
            stagger: 0.12,
            overwrite: "auto",
            clearProps: "willChange",
          });
        },
      });
      if (Array.isArray(stImg)) stImg.forEach(function (t) {
        rememberTrigger(sectionEl, t);
      });
      else rememberTrigger(sectionEl, stImg);
    }

    if (animEls.length) {
      gsap.set(animEls, { opacity: 0, y: 18, willChange: "transform,opacity" });
      var stAnim = ScrollTrigger.batch(animEls, {
        start: "top 88%",
        once: true,
        onEnter: function (batch) {
          gsap.to(batch, {
            opacity: 1,
            y: 0,
            duration: 0.85,
            ease: "power2.out",
            stagger: 0.1,
            overwrite: "auto",
            clearProps: "willChange",
          });
        },
      });
      if (Array.isArray(stAnim)) stAnim.forEach(function (t) {
        rememberTrigger(sectionEl, t);
      });
      else rememberTrigger(sectionEl, stAnim);
    }

    setTimeout(function () {
      if (typeof ScrollTrigger !== "undefined") ScrollTrigger.refresh();
    }, 250);
  }

  function initGsapLotusUspDual(root) {
    qsa(root, USP_DUAL_SECTION_SELECTOR).forEach(function (sectionEl) {
      if (sectionEl.dataset.gsapUspDualAnimated === "true") return;
      sectionEl.dataset.gsapUspDualAnimated = "true";
      initLotusUspDualSection(sectionEl);
    });
  }

  // ---------- Lotus Features Carousel ----------
  function initFeaturesCarouselSection(sectionEl) {
    if (!sectionEl) return;

    const heading = sectionEl.querySelector(FC_HEADING_SELECTOR);
    if (!heading) return;

    if (prefersReduced) {
      if (typeof gsap !== "undefined") gsap.set(heading, { opacity: 1, y: 0 });
      return;
    }

    if (!ensureGsap()) return;

    cleanup(sectionEl);

    gsap.set(heading, { opacity: 0, y: 20, willChange: "transform,opacity" });

    const st = ScrollTrigger.create({
      trigger: heading,
      start: "top 88%",
      once: true,
      onEnter: function () {
        gsap.to(heading, {
          opacity: 1,
          y: 0,
          duration: 0.85,
          ease: "power2.out",
          clearProps: "willChange",
        });
      },
    });

    rememberTrigger(sectionEl, st);

    setTimeout(function () {
      if (typeof ScrollTrigger !== "undefined") ScrollTrigger.refresh();
    }, 250);
  }

  function initGsapFeaturesCarousel(root) {
    qsa(root, FC_SECTION_SELECTOR).forEach(function (sectionEl) {
      if (sectionEl.dataset.gsapFcAnimated === "true") return;
      sectionEl.dataset.gsapFcAnimated = "true";
      initFeaturesCarouselSection(sectionEl);
    });
  }

  // ---------- Lotus Loyalty Accordion ----------
  function initLotusLoyaltySection(sectionEl) {
    if (!sectionEl) return;

    const image = sectionEl.querySelector(LA_IMAGE_SELECTOR);
    const animEls = qsa(sectionEl, LA_ANIM_SELECTOR);

    if (!image && !animEls.length) return;

    if (prefersReduced) {
      if (typeof gsap !== "undefined") {
        if (image) gsap.set(image, { opacity: 1, x: 0 });
        if (animEls.length) gsap.set(animEls, { opacity: 1, y: 0 });
      }
      return;
    }

    if (!ensureGsap()) return;

    cleanup(sectionEl);

    // Image slides in from the left
    if (image) {
      gsap.set(image, { opacity: 0, x: -32, willChange: "transform,opacity" });
      const stImg = ScrollTrigger.create({
        trigger: image,
        start: "top 88%",
        once: true,
        onEnter: function () {
          gsap.to(image, {
            opacity: 1,
            x: 0,
            duration: 0.95,
            ease: "power3.out",
            clearProps: "willChange",
          });
        },
      });
      rememberTrigger(sectionEl, stImg);
    }

    // Header + accordion items: staggered fade-up
    if (animEls.length) {
      gsap.set(animEls, { opacity: 0, y: 18, willChange: "transform,opacity" });
      const stAnim = ScrollTrigger.batch(animEls, {
        start: "top 90%",
        once: true,
        onEnter: function (batch) {
          gsap.to(batch, {
            opacity: 1,
            y: 0,
            duration: 0.75,
            ease: "power2.out",
            stagger: 0.06,
            clearProps: "willChange",
          });
        },
      });
      if (Array.isArray(stAnim)) stAnim.forEach(function (t) { rememberTrigger(sectionEl, t); });
      else rememberTrigger(sectionEl, stAnim);
    }

    setTimeout(function () {
      if (typeof ScrollTrigger !== "undefined") ScrollTrigger.refresh();
    }, 250);
  }

  function initGsapLotusLoyalty(root) {
    qsa(root, LA_SECTION_SELECTOR).forEach(function (sectionEl) {
      if (sectionEl.dataset.gsapLaAnimated === "true") return;
      sectionEl.dataset.gsapLaAnimated = "true";
      initLotusLoyaltySection(sectionEl);
    });
  }

  // ---------- Init / Shopify editor ----------
  function initAll(root = document) {
    // include root itself if it matches (editor reloads)
    if (root.matches?.(TIMELINE_SECTION_SELECTOR)) initGsapTimeline(root.parentNode || document);
    if (root.matches?.(CARD_GRID_SELECTOR)) initGsapCardReveals(root.parentNode || document);
    if (root.matches?.(USP_DUAL_SECTION_SELECTOR)) initGsapLotusUspDual(root.parentNode || document);
    if (root.matches?.(FC_SECTION_SELECTOR)) initGsapFeaturesCarousel(root.parentNode || document);
    if (root.matches?.(LA_SECTION_SELECTOR)) initGsapLotusLoyalty(root.parentNode || document);

    initGsapTimeline(root);
    initGsapCardReveals(root);
    initGsapLotusUspDual(root);
    initGsapFeaturesCarousel(root);
    initGsapLotusLoyalty(root);

    if (typeof ScrollTrigger !== "undefined") ScrollTrigger.refresh();
  }

  function findAnimatedHolder(target) {
    if (!target) return null;
    if (
      target.matches?.(TIMELINE_SECTION_SELECTOR) ||
      target.matches?.(CARD_GRID_SELECTOR) ||
      target.matches?.(USP_DUAL_SECTION_SELECTOR) ||
      target.matches?.(FC_SECTION_SELECTOR) ||
      target.matches?.(LA_SECTION_SELECTOR)
    ) {
      return target;
    }
    return (
      target.querySelector?.(TIMELINE_SECTION_SELECTOR) ||
      target.querySelector?.(CARD_GRID_SELECTOR) ||
      target.querySelector?.(USP_DUAL_SECTION_SELECTOR) ||
      target.querySelector?.(FC_SECTION_SELECTOR) ||
      target.querySelector?.(LA_SECTION_SELECTOR) ||
      null
    );
  }

  // normal load
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  // Shopify editor support
  document.addEventListener("shopify:section:load", function (e) {
    initAll(e.target);
  });

  document.addEventListener("shopify:section:unload", function (e) {
    var holder = findAnimatedHolder(e.target);
    if (holder) cleanup(holder);
  });
})();
